import React from "react";
// UI Frameworks
import { toast } from "react-toastify";
// Common Components
import { Button, Modal, StaticDropdown } from "src/Components";
import SimpleTextInput from "src/Components/SimpleTextInput";
// Local Components
import SettingsModal from "./SettingsModal";
import ConnectionsTable from "./ConnectionsTable";
// Hooks
import { useDebouncer } from "src/Hooks/useDebouncer";
import { useAppDispatch } from "src/App/hooks";
// Types
import {
  ConnectionStatus,
  getUserStatisticsResp,
  ExchangeType,
  IConnectionsSearchParams,
} from "src/Interfaces/connections";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
// Requests
import {
  getConnectionFilterOptions,
  getUserStatistics,
} from "src/Requests/connections";
import { getPortfolioGroupDropDown } from "src/Requests/portfolioGroups";
// redux
import { setSahraConnectionsTableFetchDataFlag } from "src/Features/SahraConnections/SahraConnectionsSlice";

enum ModalMode {
  Closed,
  Setting,
  ExpectedReturn,
  FilterSetting,
}

const exchangeTypeOption: DropdownItem[] = [
  { id: ExchangeType.StockExchange, name: "اوراق بهادار" },
  { id: ExchangeType.CommodityExchange, name: "کالا" },
];

const UserConnections: React.FC = () => {
  // ref
  const userStatistics = React.useRef<getUserStatisticsResp>(
    {} as getUserStatisticsResp,
  );
  // local store
  const [modalMode, setModalMode] = React.useState(ModalMode.Closed);
  const [searchParamName, setSearchParamName] = React.useState("");
  const [searchParam, setSearchParams] = React.useState<IConnectionsSearchParams>({});
  const [brokerOptions, setBrokerOptions] = React.useState<DropdownItem[]>([]);
  const [omsOptions, setOmsOptions] = React.useState<DropdownItem[]>([]);
  const [portfolioGroupOptions, setPortfolioGroupOptions] = React.useState<
    DropdownItem[]
  >([]);
  // hooks
  const dispatch = useAppDispatch();
  const searchPramNameDebounce = useDebouncer(searchParamName, 500);

  React.useEffect(() => {
    setSearchParamName(searchPramNameDebounce);
    setSearchParams({ ...searchParam, name: searchPramNameDebounce });
    getUserStatisticsHandler();
    const interval = setInterval(() => {
      getUserStatisticsHandler();
    }, 7000);
    return () => clearInterval(interval);
  }, [searchPramNameDebounce]);

  React.useEffect(() => {
    fetchAllTables();
  }, [searchParam]);

  React.useEffect(() => {
    Promise.all([getConnectionFilterOptions(), getPortfolioGroupDropDown()])
      .then(([{ brokers, oms }, portfolioGroups]) => {
        setBrokerOptions(brokers);
        setOmsOptions(oms);
        setPortfolioGroupOptions(portfolioGroups);
      })
      .catch((e) => toast.error(e.response?.data?.value ?? "خطا در دریافت فیلترها"));
  }, []);

  const fetchAllTables = () => {
    dispatch(setSahraConnectionsTableFetchDataFlag(true));
  };

  const getUserStatisticsHandler = () => {
    getUserStatistics({})
      .then((resp) => {
        userStatistics.current = resp;
      })
      .catch((e) => toast.error(e.response.data.value));
  };

  const changeHandler = (name: string, value: any) => {
    setSearchParams({ ...searchParam, [name]: value });
  };
  return (
    <div className="flex flex-col justify-start items-start gap-2 relative pt-5 mx-auto p-8 w-full">
      <div className="w-40">
        {modalMode !== ModalMode.Closed ? (
          <Modal
            title="تنظیمات"
            UI={
              <SettingsModal onClose={() => setModalMode(ModalMode.Closed)} />
            }
            onClose={() => setModalMode(ModalMode.Closed)}
            modalBoxClassName="!min-w-min"
          />
        ) : (
          <></>
        )}
        <Button
          onClick={() => setModalMode(ModalMode.Setting)}
          text="تنظیمات"
          variant="outlined"
        />
      </div>
      <div className="flex flex-row justify-between items-center w-full gap-3">
        <div className="w-44">
          <SimpleTextInput
            placeholder="اکانت"
            name="accountName"
            onChange={(_, value) => setSearchParamName(value)}
            value={searchParamName}
          />
        </div>
        <div className="w-44">
          <StaticDropdown
            dropdown={{
              name: "exchangeType",
              placeholder: "بورس",
              extraLabel: false,
            }}
            onChange={changeHandler}
            options={exchangeTypeOption}
            value={searchParam.exchangeType}
          />
        </div>
        <div className="w-44">
          <StaticDropdown
            dropdown={{
              name: "broker",
              placeholder: "کارگزاری",
              extraLabel: false,
            }}
            onChange={changeHandler}
            options={brokerOptions}
            value={searchParam.broker}
          />
        </div>
        <div className="w-44">
          <StaticDropdown
            dropdown={{
              name: "oms",
              placeholder: "OMS",
              extraLabel: false,
            }}
            onChange={changeHandler}
            options={omsOptions}
            value={searchParam.oms}
          />
        </div>
        <div className="w-44">
          <StaticDropdown
            dropdown={{
              name: "portfolioGroupId",
              placeholder: "گروه پرتفو",
              extraLabel: false,
            }}
            onChange={changeHandler}
            options={portfolioGroupOptions}
            value={searchParam.portfolioGroupId}
          />
        </div>
        <div className="flex flex-col justify-end items-end w-full">
          <div>متصل: {userStatistics?.current?.onlineUserCount}</div>
          <div className="whitespace-nowrap">
            قطع حین اتصال: {userStatistics?.current?.disconnectedUsercount}
          </div>
        </div>
      </div>
      {ConnectionsTable({
        connectionStatus: ConnectionStatus.Online,
        searchParam,
      })}
      {ConnectionsTable({
        connectionStatus: ConnectionStatus.Offline,
        searchParam,
      })}
      {ConnectionsTable({
        connectionStatus: ConnectionStatus.Disconnected,
        searchParam,
      })}
    </div>
  );
};

export default UserConnections;
